import React, { useState, useEffect, useCallback } from 'react'
import { ChevronDown, ChevronUp } from 'lucide-react'
import { useAuthContext } from '../hooks/useAuthContext'
import * as programHelper from '../helper/programHelper'
import Pump from './Pump'
import Outlet from './Outlet'
import { Device } from '../context/DeviceContext'
import { ManualControls, System } from '../context/SystemContext'
import { Button } from '@/components/ui/button'
interface ManualProgramProps {
    environment: System
    programType: string
    manualControls: ManualControls[]
    setManualControls: React.Dispatch<React.SetStateAction<ManualControls[]>>
}

const ManualProgram = ({ environment, programType, manualControls, setManualControls }: ManualProgramProps) => {
    const [pumps, setPumps] = useState<Device[]>([])
    const [outlets, setOutlets] = useState<Device[]>([])
    const [open, setOpen] = useState(true)
    const { user } = useAuthContext()

    const envId =
        environment && typeof environment === 'object' && '_id' in environment
            ? (environment as { _id: string })._id
            : null

    const loadDevices = useCallback(async () => {
        if (!user || !envId) {
            setPumps([])
            setOutlets([])
            return
        }
        const devices = await programHelper.getPumpsAndOutlets({ _id: envId } as System, user)
        setPumps(Array.isArray(devices?.[0]) ? devices[0] : [])
        setOutlets(Array.isArray(devices?.[1]) ? devices[1] : [])
    }, [user, envId])

    useEffect(() => {
        void loadDevices()
    }, [programType, loadDevices])

    const hasHardware = pumps.length > 0 || outlets.length > 0

    return (
        <div className="rounded-xl border border-zinc-800 bg-zinc-950/40">
            <div className="flex items-center justify-between gap-3 border-b border-zinc-800/90 px-4 py-3 sm:px-5">
                <div className="min-w-0">
                    <h3 className="text-sm font-semibold text-zinc-100">Manual device control</h3>
                    <p className="text-xs text-zinc-500">
                        Dose peristaltic pumps and switch outlets; values apply when you save the system.
                    </p>
                </div>
                <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="shrink-0 gap-1 text-zinc-300 hover:bg-zinc-800 hover:text-zinc-50"
                    onClick={() => setOpen((o) => !o)}
                    aria-expanded={open}
                >
                    {open ? (
                        <>
                            Hide <ChevronUp className="size-4" aria-hidden />
                        </>
                    ) : (
                        <>
                            Show <ChevronDown className="size-4" aria-hidden />
                        </>
                    )}
                </Button>
            </div>

            {open ? (
                <div className="space-y-6 border-t border-zinc-800/80 px-4 py-4 sm:px-5 sm:py-5">
                        {!hasHardware ? (
                            <p className="text-sm text-zinc-500">
                                No pumps or outlets are assigned to this system yet. Add hardware under Devices,
                                then configure roles on each device.
                            </p>
                        ) : (
                            <>
                                {pumps.length > 0 ? (
                                    <section className="space-y-3">
                                        <h4 className="text-xs font-semibold uppercase tracking-wide text-zinc-500">
                                            Pumps
                                        </h4>
                                        <div className="grid gap-4 md:grid-cols-2">
                                            {pumps.map((pump) => (
                                                <Pump
                                                    key={pump._id}
                                                    pump={pump}
                                                    manualControls={manualControls}
                                                    setManualControls={setManualControls}
                                                />
                                            ))}
                                        </div>
                                    </section>
                                ) : null}

                                {outlets.length > 0 ? (
                                    <section className="space-y-3">
                                        <h4 className="text-xs font-semibold uppercase tracking-wide text-zinc-500">
                                            Outlets
                                        </h4>
                                        <div className="grid gap-4 md:grid-cols-2">
                                            {outlets.map((outlet) => (
                                                <Outlet
                                                    key={outlet._id}
                                                    outlet={outlet}
                                                    manualControls={manualControls}
                                                    setManualControls={setManualControls}
                                                />
                                            ))}
                                        </div>
                                    </section>
                                ) : null}
                            </>
                        )}
                </div>
            ) : null}
        </div>
    )
}

export default ManualProgram
